import { useCallback, useMemo, useRef, useState } from 'react';
import { log } from '../../log/log';
import type { StatusDurationPreset, StatusVariation, UserStatus } from './types';

const ONE_MINUTE_MS = 60_000;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface UseSetStatusViewModelOptions {
  readonly initial?: UserStatus | undefined;
  readonly now?: () => Date;
}

export interface SetStatusViewModel {
  readonly emoji: string;
  readonly text: string;
  readonly preset: StatusDurationPreset;
  readonly customExpiry: Date;
  readonly preview: UserStatus;
  readonly canCommit: boolean;
  setEmoji(emoji: string): void;
  setText(text: string): void;
  setPreset(preset: StatusDurationPreset): void;
  setCustomExpiry(date: Date): void;
  commit(): UserStatus;
  clear(): UserStatus;
}

/**
 * Derive the status variation from the current emoji + text + preset state.
 * The variation is **never** a user choice — it falls out of the inputs.
 */
export function deriveVariation(args: {
  readonly emoji: string;
  readonly text: string;
  readonly hasExpiry: boolean;
}): StatusVariation {
  const hasEmoji = args.emoji.trim().length > 0;
  const hasText = args.text.trim().length > 0;
  if (hasEmoji && hasText && args.hasExpiry) return 'emojiTextDuration';
  if (hasEmoji && hasText) return 'emojiText';
  if (hasEmoji) return 'emojiOnly';
  if (hasText) return 'textOnly';
  return 'none';
}

/**
 * Resolve a preset and custom-expiry into an absolute wall-clock instant. The
 * value is consumed by `MessagingClient` consumers as a `Date`; the live
 * countdown rendered by `SetStatusModal` uses `performance.now()` deltas
 * to avoid jumping if the device clock changes mid-edit.
 */
export function resolveExpiry(
  preset: StatusDurationPreset,
  customExpiry: Date,
  now: Date,
): Date | undefined {
  switch (preset) {
    case 'thirtyMinutes':
      return new Date(now.getTime() + 30 * ONE_MINUTE_MS);
    case 'oneHour':
      return new Date(now.getTime() + 60 * ONE_MINUTE_MS);
    case 'fourHours':
      return new Date(now.getTime() + 4 * 60 * ONE_MINUTE_MS);
    case 'today': {
      const d = new Date(now);
      d.setHours(23, 59, 59, 999);
      return d;
    }
    case 'thisWeek': {
      const d = new Date(now);
      const daysUntilSunday = (7 - d.getDay()) % 7;
      d.setDate(d.getDate() + daysUntilSunday);
      d.setHours(23, 59, 59, 999);
      return d;
    }
    case 'custom': {
      const earliest = now.getTime() + ONE_MINUTE_MS;
      const ms = Math.max(
        earliest,
        Math.min(customExpiry.getTime(), now.getTime() + SEVEN_DAYS_MS),
      );
      return new Date(ms);
    }
    default:
      return undefined;
  }
}

function defaultCustomExpiry(now: Date): Date {
  return new Date(now.getTime() + 60 * ONE_MINUTE_MS);
}

export function useSetStatusViewModel(
  options: UseSetStatusViewModelOptions = {},
): SetStatusViewModel {
  const nowFn = options.now ?? (() => new Date());
  const nowRef = useRef(nowFn);
  nowRef.current = nowFn;

  const [emoji, setEmojiState] = useState(options.initial?.emoji ?? '');
  const [text, setTextState] = useState(options.initial?.text ?? '');
  const [preset, setPresetState] = useState<StatusDurationPreset>(() => {
    if (options.initial?.expiresAt) {
      return 'custom';
    }
    return 'oneHour';
  });
  const [customExpiry, setCustomExpiryState] = useState<Date>(
    () => options.initial?.expiresAt ?? defaultCustomExpiry(nowFn()),
  );

  const setEmoji = useCallback((value: string) => {
    const grapheme = firstGrapheme(value);
    setEmojiState(grapheme);
  }, []);

  const setText = useCallback((value: string) => {
    setTextState(value);
  }, []);

  const setPreset = useCallback((value: StatusDurationPreset) => {
    setPresetState(value);
  }, []);

  const setCustomExpiry = useCallback((value: Date) => {
    setCustomExpiryState(value);
  }, []);

  const preview = useMemo<UserStatus>(() => {
    const now = nowRef.current();
    const variation = deriveVariation({
      emoji,
      text,
      hasExpiry: preset !== 'custom' || customExpiry.getTime() > now.getTime(),
    });
    if (variation === 'none') {
      return { variation: 'none', setAt: now };
    }
    const expiresAt = resolveExpiry(preset, customExpiry, now);
    const status: UserStatus = {
      variation,
      setAt: now,
      ...(emoji.trim().length > 0 ? { emoji: emoji.trim() } : {}),
      ...(text.trim().length > 0 ? { text: text.trim() } : {}),
      ...(expiresAt ? { expiresAt } : {}),
    };
    return status;
  }, [emoji, text, preset, customExpiry]);

  const canCommit = preview.variation !== 'none';

  const commit = useCallback((): UserStatus => {
    log.debug('status.commit', { variation: preview.variation });
    return preview;
  }, [preview]);

  const clear = useCallback((): UserStatus => {
    const now = nowRef.current();
    log.debug('status.clear');
    setEmojiState('');
    setTextState('');
    setPresetState('oneHour');
    setCustomExpiryState(defaultCustomExpiry(now));
    return { variation: 'none', setAt: now };
  }, []);

  return useMemo<SetStatusViewModel>(
    () => ({
      emoji,
      text,
      preset,
      customExpiry,
      preview,
      canCommit,
      setEmoji,
      setText,
      setPreset,
      setCustomExpiry,
      commit,
      clear,
    }),
    [
      emoji,
      text,
      preset,
      customExpiry,
      preview,
      canCommit,
      setEmoji,
      setText,
      setPreset,
      setCustomExpiry,
      commit,
      clear,
    ],
  );
}

interface SegmenterLike {
  segment(input: string): Iterable<{ segment: string }>;
}
interface SegmenterCtor {
  new (locale: string, options: { granularity: 'grapheme' }): SegmenterLike;
}

function firstGrapheme(value: string): string {
  if (value.length === 0) {
    return '';
  }
  const ctor = (globalThis as { Intl?: { Segmenter?: SegmenterCtor } }).Intl?.Segmenter;
  if (ctor) {
    const segmenter = new ctor('en', { granularity: 'grapheme' });
    const iter = segmenter.segment(value)[Symbol.iterator]();
    const first = iter.next();
    return first.done ? '' : first.value.segment;
  }
  return value.slice(0, 2);
}
